/**
 * News of the player's games over `/ws` (packages/api-types ws.ts): one socket for the whole
 * app, opened while some game is watched. It signs in with the access token, watches every
 * game asked for, and on each change calls back with the game's new version; the caller reads
 * the game again if it is behind. A dropped socket comes back after a pause, signs in afresh
 * and watches again — and the server answers each `watch` with the current version, so
 * nothing missed meanwhile is lost.
 */
import { WsServerMessage } from '@brig/api-types';

export type OnVersion = (version: number) => void;

export interface LiveFeed {
  /** Calls `onVersion` with the game's version now and after every change, until unsubscribed. */
  watch(gameId: string, onVersion: OnVersion): () => void;
}

export interface LiveFeedOptions {
  /** `ws://` or `wss://` URL of `/ws`. */
  url: string;
  getAccessToken: () => Promise<string | null>;
  /** Tests. */
  createSocket?: (url: string) => WebSocket;
}

const RETRY_MS = [1000, 2000, 5000, 10000] as const;

/** `/ws` beside the API: the same origin unless the API lives elsewhere. */
export function liveUrl(apiBaseUrl: string, location: Pick<Location, 'protocol' | 'host'>): string {
  if (apiBaseUrl) return `${apiBaseUrl.replace(/^http/, 'ws')}/ws`;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

export function createLiveFeed(options: LiveFeedOptions): LiveFeed {
  const create = options.createSocket ?? ((url: string) => new WebSocket(url));
  const watchers = new Map<string, Set<OnVersion>>();
  let socket: WebSocket | null = null;
  let retries = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const send = (message: unknown) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  };

  function open() {
    if (socket || timer || watchers.size === 0) return;
    const ws = create(options.url);
    socket = ws;
    ws.addEventListener('open', () => {
      void options.getAccessToken().then((token) => {
        if (socket !== ws) return;
        retries = 0;
        if (token) send({ type: 'auth', token });
        for (const gameId of watchers.keys()) send({ type: 'watch', gameId });
      });
    });
    ws.addEventListener('message', (event: MessageEvent<string>) => {
      let data: unknown;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      const message = WsServerMessage.safeParse(data);
      if (!message.success || message.data.type !== 'game') return;
      const { gameId, version } = message.data;
      for (const onVersion of watchers.get(gameId) ?? []) onVersion(version);
    });
    ws.addEventListener('close', () => {
      if (socket !== ws) return;
      socket = null;
      if (watchers.size === 0) return;
      const delay = RETRY_MS[Math.min(retries, RETRY_MS.length - 1)];
      retries += 1;
      timer = setTimeout(() => {
        timer = null;
        open();
      }, delay);
    });
  }

  function close() {
    if (timer) clearTimeout(timer);
    timer = null;
    const ws = socket;
    socket = null;
    ws?.close();
  }

  return {
    watch(gameId, onVersion) {
      let set = watchers.get(gameId);
      const first = !set;
      if (!set) watchers.set(gameId, (set = new Set()));
      set.add(onVersion);
      if (first) send({ type: 'watch', gameId });
      open();
      return () => {
        set.delete(onVersion);
        if (set.size > 0) return;
        watchers.delete(gameId);
        send({ type: 'unwatch', gameId });
        if (watchers.size === 0) close();
      };
    },
  };
}
